import React, { useState, useMemo } from 'react';
import { Download, ArrowUpDown, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { FaRegFilePdf, FaRegFileWord, FaRegFileExcel } from 'react-icons/fa'; 
import { useLanguage } from '../context/LanguageContext';

const PdfNamune = () => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;

  const initialData = [
    { id: 1, name: 'कृषि कर्ज मित्र शुल्क शिफारसपत्र', type: 'pdf' },
    { id: 2, name: 'गोपीनाथ मुंडे शेतकरी अपघात विमा योजना- अर्ज', type: 'pdf' },
    { id: 3, name: 'विशेष सहाय्य योजना फाईल', type: 'pdf' },
    { id: 4, name: 'नोंदीत बांधकाम कामगाराच्या पहिल्या विवाहाच्या खर्चाच्या प्रतिपूर्तीसाठी रु.३०,०००', type: 'pdf' },
    { id: 5, name: 'बांधकाम कामगार नूतनीकरण फॉर्म (संदर्भा साठी)', type: 'pdf' },
    { id: 6, name: 'बांधकाम कामगार नोंदणी फॉर्म', type: 'pdf' },
  ];

  const [generatingId, setGeneratingId] = useState(null);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredData = useMemo(() => {
    let sortableItems = [...initialData];

    if (searchTerm) {
      sortableItems = sortableItems.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.id.toString().includes(searchTerm)
      );
    }

    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableItems;
  }, [searchTerm, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 text-gray-300 mx-1 inline" />;
    }
    return sortConfig.direction === 'ascending' 
      ? <ChevronUp className="w-4 h-4 text-gray-500 mx-1 inline" /> 
      : <ChevronDown className="w-4 h-4 text-gray-500 mx-1 inline" />;
  };

  const generatePDF = async (item) => {
    if (item.id === 1) {
      window.open('/krushi-karj-mitra-shifaraspatsa-form', '_blank');
      return;
    }

    setGeneratingId(item.id);
    // Simulate loading for now since actual files aren't linked yet
    setTimeout(() => {
      setGeneratingId(null);
      alert(language === 'marathi' ? `${item.name} डाउनलोड लवकरच उपलब्ध होईल!` : `${item.name} download will be available soon!`);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Main Card Container */}
          <div className="bg-white shadow-xl rounded-lg border border-gray-200 overflow-hidden">
            
            {/* Header Title Bar */}
            <div className="bg-[#fff1c9] border-b border-[#e5d4a1] py-2 px-4 flex justify-between items-center gap-2">
              <h2 className="text-[#000080] text-[15px] sm:text-xl font-bold flex items-center ">
            {/* Custom dark blue multi-pointed star as in the image */}
                <svg className="w-5 h-5 mr-3 text-[#000080] fill-current" viewBox="0 0 24 24">
                   <path d="M12 2l2.4 2.4L18 3.5l1.5 3.3 3.5.9-1.2 3.2 2.2 2.6-2.2 2.6 1.2 3.2-3.5.9-1.5 3.3-3.6-1.1L12 22l-2.4-2.4L6 20.5l-1.5-3.3-3.5-.9 1.2-3.2-2.2-2.6 2.2-2.6-1.2-3.2 3.5-.9 1.5-3.3 3.6 1.1z"/>
                </svg>
                {language === 'marathi' ? 'नमुने' : 'Forms'}
          </h2>
          <button onClick={() => window.history.back()} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-1 px-4 rounded text-sm no-print whitespace-nowrap shrink-0">
                  {language === 'marathi' ? 'मागे जा' : 'Back'}
                </button>
            </div>

            {/* Search Bar */}
            <div className="flex justify-end bg-white py-3 px-4 border-b border-gray-200">
              <div className="flex items-center">
                <label className="text-gray-600 mr-2 text-sm font-medium">Search:</label>
                <input
                  type="text"
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-[#337ab7] focus:ring-1 focus:ring-[#337ab7] w-48"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset page on search
                  }}
                />
              </div>
            </div>

            {/* Table Container for responsiveness */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead>
                  <tr className="bg-[#fdfdfd] border-b-2 border-gray-200 shadow-sm">
                    <th 
                      className="py-3 px-4 border-r border-gray-100 font-bold text-[#00008b] text-sm cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
                      onClick={() => handleSort('id')}
                      style={{ width: '80px' }}
                    >
                      <div className="flex items-center justify-between">
                         <span>{language === 'marathi' ? 'नं' : 'No'}</span>
                         <SortIcon columnKey="id" />
                      </div>
                    </th>
                    <th 
                      className="py-3 px-4 border-r border-gray-100 font-bold text-[#00008b] text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center justify-between">
                         <span>{language === 'marathi' ? 'नाव' : 'Name'}</span>
                         <SortIcon columnKey="name" />
                      </div>
                    </th>
                    <th className="py-3 px-4 border-r border-gray-100 font-bold text-[#00008b] text-sm text-center w-32 cursor-pointer hover:bg-gray-50 transition-colors">
                       <div className="flex items-center justify-center space-x-1">
                         <span>{language === 'marathi' ? 'प्रकार' : 'Type'}</span>
                         <ArrowUpDown className="w-4 h-4 text-gray-300 ml-1 inline" />
                       </div>
                    </th>
                    <th className="py-3 px-4 font-bold text-[#00008b] text-sm text-center w-48 cursor-pointer hover:bg-gray-50 transition-colors">
                       <div className="flex items-center justify-center space-x-1">
                         <span>{language === 'marathi' ? 'डाऊनलोड करा' : 'Download'}</span>
                         <ArrowUpDown className="w-4 h-4 text-gray-300 ml-1 inline" />
                       </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr 
                         key={item.id} 
                         className={`border-b border-gray-200 hover:bg-[#f8fafc] transition-colors ${index % 2 === 0 ? 'bg-[#fbfbfb]' : 'bg-white'}`}
                      >
                        <td className="py-2.5 px-4 text-sm text-[#000080] border-r border-gray-100 text-left font-medium">
                          {item.id}
                        </td>
                        <td className="py-2.5 px-4 text-sm text-[#4a729e] border-r border-gray-100">
                          {item.name}
                        </td>
                        <td className="py-2.5 px-4 text-center border-r border-gray-100">
                          {item.type === 'word' ? (
                             <FaRegFileWord className="w-5 h-5 text-[#000080] mx-auto opacity-75" />
                          ) : item.type === 'excel' ? (
                             <FaRegFileExcel className="w-5 h-5 text-[#000080] mx-auto opacity-75" />
                          ) : (
                             <FaRegFilePdf className="w-5 h-5 text-[#000080] mx-auto opacity-75" />
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          <button 
                            onClick={() => generatePDF(item)}
                            disabled={generatingId === item.id}
                            className={`text-white text-xs px-3 py-1.5 rounded-sm flex items-center justify-center mx-auto space-x-1 shadow-sm transition-all flex-nowrap w-36 ${
                              generatingId === item.id 
                                ? 'bg-gray-500 cursor-not-allowed' 
                                : 'bg-[#00a65a] hover:bg-[#008d4c]'
                            }`}
                          >
                            {generatingId === item.id ? (
                               <Loader className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                               <Download className="w-3.5 h-3.5" />
                            )}
                            <span className="font-semibold whitespace-nowrap">
                              {generatingId === item.id ? 'Loading...' : 'View/Download'}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-6 text-center text-gray-500">
                        {language === 'marathi' ? 'कोणताही नमुना सापडला नाही.' : 'No forms found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-end items-center bg-gray-50 px-4 py-3 border-t border-gray-200">
                <div className="flex space-x-1 shadow-sm">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1 text-sm rounded-l-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {/* Dynamically generate page numbers */}
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`${
                        currentPage === i + 1 
                        ? 'bg-[#337ab7] text-white border-[#337ab7]' 
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      } border-y border-r px-3 py-1 text-sm`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="bg-white border-y border-r border-gray-300 text-[#337ab7] hover:bg-gray-50 px-3 py-1 text-sm rounded-r-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
};

export default PdfNamune;
